import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, saveToken, clearToken, formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

function pickUser(data) {
  return {
    id: data.id,
    email: data.email,
    name: data.name,
    role: data.role || "user",
    whatsapp_number: data.whatsapp_number || "",
    picture: data.picture || "",
  };
}

export function AuthProvider({ children }) {
  // null = checking, false = logged out, object = logged in
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(pickUser(data));
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback exchanges session_id first.
    if (window.location.hash?.includes("session_id=")) return;
    refresh();
  }, [refresh]);

  const googleLogin = useCallback(async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      saveToken(data.token);
      setUser(pickUser(data));
      return { ok: true, user: pickUser(data) };
    } catch (e) {
      setUser(false);
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const saveWhatsapp = async (whatsapp_number) => {
    try {
      const { data } = await api.put("/auth/whatsapp", { whatsapp_number });
      setUser(pickUser(data));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveToken(data.token);
      setUser(pickUser(data));
      return { ok: true, user: pickUser(data) };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async ({ name, email, password, whatsapp_number }) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, whatsapp_number });
      saveToken(data.token);
      setUser(pickUser(data));
      return { ok: true, user: pickUser(data) };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("vx-test-progress");
    clearToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh, googleLogin, saveWhatsapp }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
